import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const PASSING_SCORE = 80; // 80% to pass

export const useQuizAttempts = () => {
  const queryClient = useQueryClient();

  const submitQuiz = useMutation({
    mutationFn: async ({
      assignmentId,
      answers,
      correctAnswers,
    }: {
      assignmentId: string;
      answers: number[];
      correctAnswers: number[];
    }) => {
      const score = answers.reduce((acc, answer, index) => {
        return acc + (answer === correctAnswers[index] ? 1 : 0);
      }, 0);

      const percentage = (score / correctAnswers.length) * 100;
      const passed = percentage >= PASSING_SCORE;

      // Insert quiz attempt
      const { error: attemptError } = await supabase
        .from('training_quiz_attempts')
        .insert({
          assignment_id: assignmentId,
          answers,
          score,
          total_questions: correctAnswers.length,
          passed,
        });

      if (attemptError) throw attemptError;

      // If passed, update assignment status to completed
      if (passed) {
        const { error: updateError } = await supabase
          .from('training_assignments')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          .eq('id', assignmentId);

        if (updateError) throw updateError;

        // Get assignment details to unlock next module
        const { data: assignment } = await supabase
          .from('training_assignments')
          .select('seller_id, module_id, training_modules!inner(order_position)')
          .eq('id', assignmentId)
          .single();

        if (assignment) {
          // Find and unlock next module
          const { data: nextAssignment } = await supabase
            .from('training_assignments')
            .select('id, training_modules!inner(order_position)')
            .eq('seller_id', assignment.seller_id)
            .eq('status', 'locked')
            .order('training_modules(order_position)', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (nextAssignment) {
            await supabase
              .from('training_assignments')
              .update({ status: 'pending' })
              .eq('id', nextAssignment.id);
          }
        }
      }

      return { score, totalQuestions: correctAnswers.length, percentage, passed };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
      if (result.passed) {
        toast.success(`¡Felicitaciones! Aprobaste con ${result.percentage.toFixed(0)}%`);
      } else {
        toast.error(`No aprobaste (${result.percentage.toFixed(0)}%). Necesitas 80% para pasar.`);
      }
    },
    onError: (error) => {
      toast.error('Error al enviar quiz: ' + error.message);
    },
  });

  return { submitQuiz };
};
